import { Button, ButtonTheme } from 'components/Button';
import { Card, CardBorderRadius } from 'components/Card';
import { DateField } from 'components/Field/DateField';
import { ICONS } from 'components/Icon';
import { IconButton } from 'components/IconButton';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { Pressable } from 'components/Pressable';
import { Txt } from 'components/Txt';
import { useEsc } from 'hooks/useEsc';
import {
  API_DATE_FORMAT,
  ScreenSize,
  UI_DATE_FORMAT_LONG,
} from 'lib/constants';
import {
  randomKey,
  dateToIsoString,
  isoDateStringToDate,
  arrayJoin,
} from 'lib/riskbase';
import { theme } from 'lib/theme';
import { addDays, format, parseISO, startOfDay } from 'date-fns';
import * as React from 'react';
import { LayoutChangeEvent, View } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  ALERT,
  doToastError,
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
} from 'store/actions';
import { doEngageScheduledVisitSave } from 'store/remote/engageActions';
import {
  EngageScheduledVisitStatus,
  BlockManagerScheduledVisitTimeslot,
} from 'types/DTOs/BlockManagerDTOs';
import {
  EngageInspector,
  EngageScheduledVisitListItem,
  SCHEDULED_VISIT_COMMUNICATION_REQUEST_DAYS,
} from 'types/Engage';
import { ISODateString } from 'types/types';
import { useScreenSize } from 'hooks/useScreenSize';
import { EngageScheduledVisitInspectors } from './Components/EngageScheduledVisitInspectors';
import { createId } from '@paralleldrive/cuid2';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { pluralise } from 'lib/pluralise';
import { CardRow } from 'components/CardRow';
import { chunk } from 'lodash';

type ScheduleDate = {
  id: string;
  dateString: ISODateString;
  hours: number[];
};

// const HOURS = Array.from({ length: 24 }, (_, index) => index);
const HOURS = [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];
const HOURS_AM = HOURS.filter((h) => h < 13);
const HOURS_PM = HOURS.filter((h) => h >= 13);

type SetHourSelection = (
  dateId: string,
  hour: number,
  shouldSelect: boolean | null,
) => void;
type HandleStartDrag = (
  dateId: string,
  hour: number,
  isSelected: boolean,
) => void;
type HandleEnterDuringDrag = (dateId: string, hour: number) => void;
type HandleEndDrag = () => void;

const buildScheduleDatesFromSlots = (
  slots: EngageScheduledVisitListItem['slots'],
): ScheduleDate[] =>
  slots.map((slot) => ({
    id: createId(),
    dateString: slot.date,
    hours: [...slot.hours],
  }));

export const EngageFlatDoorScheduledVisitModal: React.FC<
  ModalScreenProps<ModalScreens.EngageFlatDoorScheduledVisit>
> = (props) => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize();

  const { propertyId, pageKey, scheduledVisit } = props.route.params;
  const isPublished =
    scheduledVisit?.status === EngageScheduledVisitStatus.PUBLISHED;
  const isCancelled =
    scheduledVisit?.status === EngageScheduledVisitStatus.CANCELLED;

  // View only can accomodate other variables in the future
  const isViewOnly = isPublished || isCancelled;

  const canSaveDraft = !isPublished && !isCancelled;
  const canSavePublished = isPublished;
  const canCancel = isPublished;
  const canPublish = !!scheduledVisit && !isPublished && !isCancelled;

  const pendingDateIsCustomisedRef = React.useRef(false);

  /**
   * pendingDate is automatically set to lastDate+1day when scheduledDates
   * is updated. Unless pendingDateIsCustomisedRef=true as the user has
   * explicitly selected it
   */
  const [pendingDate, setPendingDate] = React.useState(
    format(
      scheduledVisit?.endDateTime
        ? addDays(isoDateStringToDate(scheduledVisit?.endDateTime), 1)
        : addDays(new Date(), SCHEDULED_VISIT_COMMUNICATION_REQUEST_DAYS),
      API_DATE_FORMAT,
    ),
  );

  // Specific callback for date field to mark pendingDate as customised
  const setPendingDateFromDateField = React.useCallback((value: string) => {
    pendingDateIsCustomisedRef.current = true;
    setPendingDate(value);
  }, []);

  const [copiedHours, setCopiedHours] = React.useState<number[]>([]);
  const [copyId, setCopyId] = React.useState<string | null>(null);
  const [scheduleDates, setScheduleDates] = React.useState<ScheduleDate[]>(
    scheduledVisit ? buildScheduleDatesFromSlots(scheduledVisit.slots) : [],
  );
  const isDraggingRef = React.useRef(false);
  const dragTargetRef = React.useRef<boolean | null>(null);
  const today = React.useMemo(() => startOfDay(new Date()), []);
  const [inspectors, setInspectors] = React.useState<EngageInspector[]>(
    scheduledVisit?.inspectors ?? [],
  );

  const handleClose = React.useCallback(() => {
    dispatch(NAVIGATE_MODAL_BACK());
  }, [dispatch]);

  useEsc(handleClose);

  React.useEffect(() => {
    if (pendingDateIsCustomisedRef.current) {
      return;
    }
    const newPendingDate =
      scheduleDates.reduce<Date | null>((acc, scheduleDate) => {
        const thisDate = isoDateStringToDate(scheduleDate.dateString);
        if (!acc || thisDate >= acc) {
          return addDays(thisDate, 1);
        }
        return acc;
      }, null) ||
      addDays(new Date(), SCHEDULED_VISIT_COMMUNICATION_REQUEST_DAYS);
    setPendingDate(format(newPendingDate, API_DATE_FORMAT));
  }, [scheduleDates]);

  const handleAddDate = React.useCallback(
    (newDateString: string) => {
      const newIsoDateString = dateToIsoString(parseISO(newDateString));
      // Error if this date has already be added
      const matchedDate = scheduleDates.some(
        (item) => item.dateString === newIsoDateString,
      );
      if (matchedDate) {
        dispatch(doToastError('This date is already selected'));
        return;
      }

      // The user has added this date so pendingDate becomes non-customised
      pendingDateIsCustomisedRef.current = false;

      // Add new date
      setScheduleDates((current) => [
        ...current,
        {
          id: randomKey(),
          dateString: newIsoDateString,
          hours: [],
        },
      ]);
    },
    [scheduleDates, dispatch],
  );

  const handleUpdateDate = React.useCallback(
    (index: number, newDateString: string) => {
      if (!newDateString) {
        return;
      }
      const newIsoDateString = dateToIsoString(parseISO(newDateString));
      // Error if this date has already be added
      const matchedDate = scheduleDates.some(
        (sd, i) => i !== index && sd.dateString === newIsoDateString,
      );
      if (matchedDate) {
        dispatch(doToastError('This date is already selected'));
        return;
      }
      // Update existing date
      setScheduleDates(
        scheduleDates.map((sd, i) =>
          i !== index ? sd : { ...sd, dateString: newIsoDateString },
        ),
      );
    },
    [scheduleDates, dispatch],
  );

  const handleCopy = React.useCallback(
    (index: number) => {
      const schedule = scheduleDates[index];
      if (!schedule) {
        return;
      }
      setCopiedHours(schedule.hours);
      setCopyId(schedule.id);
    },
    [scheduleDates],
  );

  const handleCancelCopy = React.useCallback(() => {
    setCopiedHours([]);
    setCopyId(null);
  }, []);

  const handlePaste = React.useCallback(
    (index: number) => {
      setScheduleDates((current) =>
        current.map((sd, i) =>
          i !== index
            ? sd
            : {
                ...sd,
                hours: [...copiedHours],
              },
        ),
      );
    },
    [copiedHours],
  );

  const setHourSelection = React.useCallback<SetHourSelection>(
    (dateId, hour, shouldSelect) => {
      setCopyId(null);
      setScheduleDates((current) =>
        current.map((item) => {
          if (item.id !== dateId) {
            return item;
          }
          const isSelected = item.hours.includes(hour);
          const nextState =
            shouldSelect === null ? !isSelected : shouldSelect === true;
          const updatedHours =
            nextState === isSelected
              ? item.hours
              : nextState
                ? [...item.hours, hour].sort((a, b) => a - b)
                : item.hours.filter((value) => value !== hour);
          return {
            ...item,
            hours: updatedHours,
          };
        }),
      );
    },
    [],
  );

  const handleStartDrag = React.useCallback<HandleStartDrag>(
    (dateId, hour, isSelected) => {
      const target = !isSelected;
      dragTargetRef.current = target;
      isDraggingRef.current = true;
      setHourSelection(dateId, hour, target);
    },
    [setHourSelection],
  );

  const handleEnterDuringDrag = React.useCallback<HandleEnterDuringDrag>(
    (dateId, hour) => {
      if (!isDraggingRef.current || dragTargetRef.current === null) {
        return;
      }
      setHourSelection(dateId, hour, dragTargetRef.current);
    },
    [setHourSelection],
  );

  const handleEndDrag = React.useCallback<HandleEndDrag>(() => {
    isDraggingRef.current = false;
    dragTargetRef.current = null;
  }, []);

  const handleCancel = React.useCallback(() => {
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.EngageCancelScheduledVisit,
        params: {
          propertyId: propertyId,
          pageKey: pageKey,
          scheduledVisitId: scheduledVisit!.id,
        },
      }),
    );
  }, [dispatch, pageKey, propertyId, scheduledVisit]);

  const handleSave = React.useCallback(
    (status: EngageScheduledVisitStatus) => {
      // Prepare timeslots for API
      const timeslots = scheduleDates.map<BlockManagerScheduledVisitTimeslot>(
        (item) => ({
          date: item.dateString,
          hours: item.hours,
        }),
      );

      const hasDateWithoutHours = scheduleDates.some(
        (item) => item.hours.length === 0,
      );
      const hasPastDates = scheduleDates.some(
        (item) => startOfDay(parseISO(item.dateString)) < today,
      );

      // Validate before save
      const errors: string[] = [];
      if (!scheduleDates.length) {
        errors.push('at least one date is required');
      } else if (hasDateWithoutHours) {
        errors.push('all dates must have at least one hour selected');
      }
      if (hasPastDates && status === EngageScheduledVisitStatus.PUBLISHED) {
        errors.push('all dates must be in the future');
      }

      if (errors.length) {
        const verb = !scheduledVisit
          ? 'Save'
          : status === EngageScheduledVisitStatus.PUBLISHED &&
              scheduledVisit.status !== EngageScheduledVisitStatus.PUBLISHED
            ? 'Publish'
            : 'Save';
        const verbPastParticiple = !scheduledVisit
          ? 'created'
          : status === EngageScheduledVisitStatus.PUBLISHED &&
              scheduledVisit?.status !== EngageScheduledVisitStatus.PUBLISHED
            ? 'published'
            : 'updated';
        dispatch(
          ALERT({
            title: `Cannot ${verb}`,
            text: `This scheduled visit cannot be ${verbPastParticiple} because ${arrayJoin(errors, ', ', ' and ')}`,
          }),
        );
        return;
      }

      dispatch(
        doEngageScheduledVisitSave({
          propertyId,
          pageKey,
          timeslots,
          scheduledVisitId: scheduledVisit?.id,
          status,
          inspectorIds: inspectors.map((inspector) => inspector.id),
          currentStatus:
            scheduledVisit?.status ?? EngageScheduledVisitStatus.DRAFT,
        }),
      );
    },
    [
      scheduleDates,
      dispatch,
      propertyId,
      pageKey,
      scheduledVisit,
      inspectors,
      today,
    ],
  );

  const handleRemoveDate = React.useCallback(
    (dateId: string) => {
      if (dateId === copyId) {
        setCopyId(null);
      }
      setScheduleDates((current) =>
        current.filter((item) => item.id !== dateId),
      );
    },
    [copyId],
  );

  return (
    <PageScrollView
      header={
        <NavigationHeader
          heading={'Scheduled Visit'}
          subheading={
            screenSize === ScreenSize.small
              ? 'FED Check'
              : 'Flat Entrance Door Check'
          }
          cancelButtonText="Close"
          onPressCancelButton={handleClose}
          buttons={
            canSaveDraft || canSavePublished || canCancel || canPublish ? (
              <View
                style={{
                  flexDirection: 'row',
                  columnGap: theme.paddingHorizontal,
                  alignItems: 'center',
                }}
              >
                {canSaveDraft && (
                  <Button
                    text="Save Draft"
                    buttonTheme={ButtonTheme.secondary}
                    onPress={() => handleSave(EngageScheduledVisitStatus.DRAFT)}
                  />
                )}
                {canSavePublished && (
                  <Button
                    text="Save"
                    buttonTheme={ButtonTheme.secondary}
                    onPress={() =>
                      handleSave(EngageScheduledVisitStatus.PUBLISHED)
                    }
                  />
                )}
                {canPublish && (
                  <Button
                    text="Publish"
                    buttonTheme={ButtonTheme.primary}
                    onPress={() =>
                      handleSave(EngageScheduledVisitStatus.PUBLISHED)
                    }
                  />
                )}
                {canCancel && (
                  <Button
                    text="Cancel"
                    buttonTheme={ButtonTheme.primary}
                    styleHovered={{ backgroundColor: theme.colors.red80 }}
                    stylePressed={{ backgroundColor: theme.colors.red80 }}
                    textStyle={{ color: theme.colors.white }}
                    style={{
                      backgroundColor: theme.colors.red,
                      borderColor: theme.colors.red,
                    }}
                    onPress={handleCancel}
                  />
                )}
              </View>
            ) : undefined
          }
        />
      }
    >
      {scheduledVisit?.status === EngageScheduledVisitStatus.CANCELLED ? (
        <Card>
          <CardRow
            label="Cancelled"
            labelColor={theme.colors.red}
            labelAlignTop
            value="This visit's details can no longer be updated."
          />
        </Card>
      ) : (
        scheduledVisit?.status === EngageScheduledVisitStatus.PUBLISHED && (
          <Card>
            <CardRow
              label="Published"
              labelColor={theme.colors.green}
              labelAlignTop
              value="This visit's dates can no longer be changed. The inspector team can still be updated."
            />
          </Card>
        )
      )}
      <Card label="Schedule Timeline" labelSize="large" margin={false}></Card>
      <View style={{ gap: theme.paddingHorizontalHalf }}>
        {scheduleDates.map((item, index) => (
          <Card key={item.id} withShadow={false} margin={false}>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                padding: theme.paddingHorizontal,
                backgroundColor: theme.colors.white,
                gap: theme.paddingHorizontal,
                pointerEvents: isViewOnly ? 'none' : 'auto',
              }}
            >
              {isViewOnly ? (
                <Txt>{format(item.dateString, UI_DATE_FORMAT_LONG)}</Txt>
              ) : (
                <DateField
                  value={format(item.dateString, API_DATE_FORMAT)}
                  onChange={(value) => handleUpdateDate(index, value)}
                  placeholder="Select a date"
                  min={
                    // This stops the date resetting on field blur if opening
                    // an existing visit with dates older than today
                    isoDateStringToDate(item.dateString) < today
                      ? isoDateStringToDate(item.dateString)
                      : today
                  }
                  clearable
                  showFieldTypeIcon={false}
                  showClearButton={false}
                  shrinkToWidth
                  style={{
                    minHeight: 0,
                    paddingLeft: 0,
                    paddingRight: 0,
                    backgroundColor: 'transparent',
                    borderRadius: 8,
                  }}
                />
              )}
              <Txt
                size="small"
                style={{
                  color: theme.colors.fieldValueText,
                  flex: 1,
                }}
              >
                {pluralise(
                  item.hours.length,
                  '{x} Slot',
                  '{x} Slots',
                  'No Slots',
                )}
              </Txt>
              {!isViewOnly && (
                <View
                  style={{
                    flexDirection: 'row',
                    gap: theme.paddingHorizontal / 2,
                  }}
                >
                  {scheduleDates.length > 1 &&
                    (!copyId || copyId !== item.id) && (
                      <Button
                        buttonTheme={ButtonTheme.link}
                        onPress={() => handleCopy(index)}
                        text="Copy"
                        chipLike
                      />
                    )}
                  {scheduleDates.length > 1 &&
                    !!copyId &&
                    (copyId !== item.id ? (
                      <Button
                        buttonTheme={ButtonTheme.link}
                        onPress={() => handlePaste(index)}
                        text="Paste"
                        chipLike
                      />
                    ) : (
                      <Button
                        buttonTheme={ButtonTheme.secondary}
                        onPress={handleCancelCopy}
                        text="Cancel Copy"
                        destructive
                        chipLike
                      />
                    ))}
                  <IconButton
                    icon={ICONS.TrashCanLight}
                    onPress={() => handleRemoveDate(item.id)}
                  />
                </View>
              )}
            </View>
            <ViewWithSeparators
              horizontal
              style={{
                backgroundColor: theme.colors.white80,
                gap: theme.paddingHorizontal / 4,
                flexDirection: 'row',
              }}
            >
              <HourGroup
                scheduleDate={item}
                key="morning"
                title="Morning"
                hours={HOURS_AM}
                isViewOnly={isViewOnly}
                setHourSelection={setHourSelection}
                handleEndDrag={handleEndDrag}
                handleEnterDuringDrag={handleEnterDuringDrag}
                handleStartDrag={handleStartDrag}
              />
              <HourGroup
                scheduleDate={item}
                key="morning"
                title="Morning"
                hours={HOURS_PM}
                isViewOnly={isViewOnly}
                setHourSelection={setHourSelection}
                handleEndDrag={handleEndDrag}
                handleEnterDuringDrag={handleEnterDuringDrag}
                handleStartDrag={handleStartDrag}
              />
            </ViewWithSeparators>
          </Card>
        ))}
        {!isViewOnly && (
          <Card margin={false}>
            <CardRow
              contentStyle={{
                paddingTop: theme.paddingHorizontalHalf,
                paddingBottom: theme.paddingHorizontalHalf,
              }}
            >
              <ViewWithSeparators
                horizontal
                separatorMargin={theme.paddingHorizontalHalf}
                separatorColor="transparent"
                style={{ alignItems: 'center', justifyContent: 'center' }}
              >
                <DateField
                  value={pendingDate}
                  onChange={setPendingDateFromDateField}
                  clearable={false}
                  min={today}
                  showFieldTypeIcon={false}
                  shrinkToWidth
                  style={{
                    paddingLeft: 0,
                    paddingRight: 0,
                    minHeight: 0,
                  }}
                />
                <Button
                  text={'Add Date'}
                  icon={ICONS.PlusLight}
                  buttonTheme={ButtonTheme.primary}
                  onPress={() => handleAddDate(pendingDate)}
                />
              </ViewWithSeparators>
            </CardRow>
          </Card>
        )}
      </View>

      <View style={{ height: theme.paddingVertical }}></View>

      <EngageScheduledVisitInspectors
        propertyId={propertyId}
        inspectors={inspectors}
        onInspectorsChanged={setInspectors}
        canEdit={!isCancelled}
      />

      <View style={{ height: 50 }}></View>
    </PageScrollView>
  );
};

const HourGroup = React.memo(
  ({
    scheduleDate,
    title,
    hours,
    isViewOnly,
    handleEnterDuringDrag,
    handleStartDrag,
    setHourSelection,
    handleEndDrag,
  }: {
    scheduleDate: ScheduleDate;
    title: string;
    hours: number[];
    isViewOnly: boolean;
    setHourSelection: SetHourSelection;
    handleStartDrag: HandleStartDrag;
    handleEnterDuringDrag: HandleEnterDuringDrag;
    handleEndDrag: HandleEndDrag;
  }) => {
    const [rows, setRows] = React.useState(2);
    const handleLayout = (event: LayoutChangeEvent) => {
      setRows(event.nativeEvent.layout.width < 350 ? 2 : 1);
    };

    return (
      <View
        key={`${scheduleDate.id}-${title}`}
        style={{
          flex: 1,
          padding: theme.paddingHorizontal,
        }}
        onLayout={handleLayout}
      >
        <View style={{ gap: theme.paddingHorizontal / 4 }}>
          {chunk(hours, hours.length / rows).map((hoursChunk) => (
            <View
              key={hoursChunk.join('-')}
              style={{
                flexDirection: 'row',
                gap: theme.paddingHorizontal / 4,
              }}
            >
              {hoursChunk.map((hour) => (
                <HourBlock
                  key={hour}
                  hour={hour}
                  dateId={scheduleDate.id}
                  isViewOnly={isViewOnly}
                  isSelected={scheduleDate.hours.includes(hour)}
                  handleEnterDuringDrag={handleEnterDuringDrag}
                  handleStartDrag={handleStartDrag}
                  setHourSelection={setHourSelection}
                  handleEndDrag={handleEndDrag}
                />
              ))}
            </View>
          ))}
        </View>
      </View>
    );
  },
);

const HourBlock = React.memo(
  ({
    dateId,
    hour,
    isSelected,
    isViewOnly,
    handleEnterDuringDrag,
    handleStartDrag,
    setHourSelection,
    handleEndDrag,
  }: {
    hour: number;
    dateId: string;
    isSelected: boolean;
    isViewOnly: boolean;
    setHourSelection: SetHourSelection;
    handleStartDrag: HandleStartDrag;
    handleEnterDuringDrag: HandleEnterDuringDrag;
    handleEndDrag: HandleEndDrag;
  }) => {
    const interactionProps = isViewOnly
      ? {}
      : {
          onPress: () => setHourSelection(dateId, hour, isSelected),
          onPressIn: () => handleStartDrag(dateId, hour, isSelected),
          onPressOut: handleEndDrag,
          onMoveShouldSetResponder: () => true,
          onStartShouldSetResponder: () => true,
          onResponderTerminationRequest: () => false,
          onResponderGrant: () => handleEnterDuringDrag(dateId, hour),
          onResponderMove: () => handleEnterDuringDrag(dateId, hour),
          onResponderRelease: handleEndDrag,
          onResponderTerminate: handleEndDrag,
          onMouseEnter: () => handleEnterDuringDrag(dateId, hour),
        };
    const AMPM = hour < 12 ? 'am' : 'pm';
    return (
      <Pressable
        key={hour}
        disabled={isViewOnly}
        pointerEvents={isViewOnly ? 'none' : undefined}
        {...interactionProps}
        style={{
          flex: 1,
          height: 50,
          borderRadius: CardBorderRadius,
          borderWidth: 1,
          borderColor: isSelected ? theme.colors.green : theme.colors.black20,
          backgroundColor: isSelected ? theme.colors.green : theme.colors.white,
          alignItems: 'center',
          justifyContent: 'center',
          opacity: !isSelected && isViewOnly ? 0.42 : 1,
        }}
        hoveredStyle={{
          borderColor: isSelected ? theme.colors.green80 : theme.colors.green10,
          backgroundColor: isSelected
            ? theme.colors.green80
            : theme.colors.green10,
        }}
        pressedStyle={{
          borderColor: isSelected ? theme.colors.green80 : theme.colors.green20,
          backgroundColor: isSelected
            ? theme.colors.green80
            : theme.colors.green20,
        }}
      >
        <Txt
          weight="medium"
          style={{
            color: isSelected ? 'white' : theme.colors.text,
          }}
          selectable={false}
          numberOfLines={1}
        >
          {hour === 0 ? '12' : hour < 13 ? hour : hour - 12}
          <Txt
            size="xsmall"
            weight="medium"
            style={{
              color: isSelected ? 'white' : theme.colors.text,
              opacity: 0.4,
            }}
            selectable={false}
          >
            {' '}
            {AMPM}
          </Txt>
        </Txt>
      </Pressable>
    );
  },
);
